import React, { useMemo } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  Typography,
  Checkbox,
  ListItemText,
} from '@mui/material';

import { LocalizationContext } from '@cvt/contexts';
import { useMe } from '@modules/Auth/hooks/useMe';


export type PreferenceSelectProps = Omit<
  SelectProps<string | string[] | null>,
  'renderValue'
> & {
  error?: boolean;
  helperText?: React.ReactNode;
  field: 'objectives' | 'values';
};

export const PreferenceSelect = React.forwardRef<HTMLDivElement, PreferenceSelectProps>(
  ({ error, helperText, label, slotProps, field, multiple, defaultValue, ...props }, ref) => {
    const { dictionary } = React.useContext(LocalizationContext);
    const { user } = useMe();

    const options = useMemo(() => {
      let list: Array<CVT.Maybe<string>> = [...defaultValue || []];
      if (field === 'objectives') {
        list = [
          user?.preferences?.communicationObjective1,
          user?.preferences?.communicationObjective2,
          user?.preferences?.communicationObjective3,
        ];
      } else {
        list = [
          user?.preferences?.leadershipValue1,
          user?.preferences?.leadershipValue2,
          user?.preferences?.leadershipValue3,
        ];
      }
      return list.filter(Boolean) as string[];
    }, [field, user, defaultValue]);

    const placeholder =
      field === 'objectives'
        ? dictionary.forms.story.linkedObjectivesPlaceholder
        : dictionary.forms.story.linkedValuesPlaceholder;

    return (
      <Box width="100%">
        <FormControl fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple={multiple}
            displayEmpty
            label={label}
            {...props}
            ref={ref}
            color="primary"
            renderValue={(selected) => {
              if (!selected || (Array.isArray(selected) && selected.length === 0)) {
                return <span style={{ color: '#a2a2a2' }}>{placeholder}</span>;
              }

              if (Array.isArray(selected)) {
                return (
                  <Typography noWrap textOverflow="ellipsis">
                    {selected.join(', ')}
                  </Typography>
                );
              }

              return (
                <Typography noWrap textOverflow="ellipsis">
                  {selected}
                </Typography>
              );
            }}
            MenuProps={{
              ...props.MenuProps,
              PaperProps: {
                ...props.MenuProps?.PaperProps,
                sx: {
                  maxHeight: 36 * 8.5 + 8,
                },
              },
            }}
            error={error}
          >
            {!multiple && (
              <MenuItem value="">
                <span style={{ color: '#a2a2a2' }}>{placeholder}</span>
              </MenuItem>
            )}
            {options.map((option) => (
              <MenuItem key={option} value={option} sx={{ maxWidth: 500 }}>
                {multiple ? (
                  <>
                    <Checkbox checked={(props.value as string[])?.includes(option)}/>
                    <ListItemText slotProps={{  }} primary={option} sx={{ whiteSpace: 'wrap' }}/>
                  </>
                ) : (
                  <Typography variant="caption" sx={{ whiteSpace: 'wrap' }}>
                    {option}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {!!helperText && (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        )}
      </Box>
    );
  },
);
